import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';

// Suppress ResizeObserver noisy errors in dev console (small, opt-in hook)
export function useResizeObserverErrorSuppression() {
  useEffect(() => {
    const originalError = window.console.error;
    window.console.error = (...args) => {
      const errorMessage = args[0];
      if (
        typeof errorMessage === 'string' &&
        (errorMessage.includes('ResizeObserver loop') || errorMessage.includes('findDOMNode is deprecated'))
      ) {
        return;
      }
      originalError.apply(window.console, args);
    };
    return () => { window.console.error = originalError; };
  }, []);
}

// Responsive drawer width helper
export function useResponsiveDrawerWidth() {
  const [drawerWidth, setDrawerWidth] = useState(500);
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 600) setDrawerWidth('100vw');
      else if (window.innerWidth < 900) setDrawerWidth(360);
      else setDrawerWidth(500);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return drawerWidth;
}

// Formatting / parsing helpers
export function formatCategories(categories, maxItems = 2, maxLength = 40) {
  if (!categories) return { display: '', full: '' };
  let arr = [];
  if (typeof categories === 'string') {
    try { arr = JSON.parse(categories); } catch { arr = categories.split(',').map(c => c.trim()); }
  } else if (Array.isArray(categories)) arr = categories; else arr = [String(categories)];
  const full = arr.join(', ');
  let display = full;
  if (arr.length > maxItems) display = arr.slice(0, maxItems).join(', ') + '...';
  if (display.length > maxLength) display = display.slice(0, maxLength).trim() + '...';
  return { display, full };
}

export function parseCategories(categories) {
  if (!categories) return [];
  if (typeof categories === 'string') {
    try { return JSON.parse(categories); } catch { return categories.split(',').map(c => c.trim()); }
  } else if (Array.isArray(categories)) return categories;
  return [];
}

export function parseQualifications(qualification) {
  if (!qualification) return [];
  if (typeof qualification === 'string') {
    try { return JSON.parse(qualification); } catch { return qualification.split(',').map(q => q.trim()); }
  } else if (Array.isArray(qualification)) return qualification; else return [qualification];
}

export function parseServices(offered_services) {
  if (!offered_services) return [];
  if (typeof offered_services === 'string') {
    try { return JSON.parse(offered_services); } catch { return offered_services.split(',').map(s => s.trim()); }
  } else if (Array.isArray(offered_services)) return offered_services; else return [offered_services];
}

export function parseGalleryImages(urlGallery) {
  if (!urlGallery) return [];
  if (typeof urlGallery === 'string') {
    try { return JSON.parse(urlGallery); } catch { return urlGallery.split(',').map(url => url.trim()).filter(Boolean); }
  } else if (Array.isArray(urlGallery)) return urlGallery;
  return [];
}

export function calculateAverageRating(reviews) {
  if (!reviews) return 0;
  let reviewsArr = [];
  if (typeof reviews === 'string') {
    try { reviewsArr = JSON.parse(reviews); } catch { reviewsArr = []; }
  } else if (Array.isArray(reviews)) reviewsArr = reviews;
  if (reviewsArr.length === 0) return 0;
  const ratings = reviewsArr.map(r => { let val = r.rating; if (typeof val === 'string') val = parseFloat(val); if (typeof val === 'number' && val >= 1 && val <= 5) return val; return null; }).filter(r => r !== null);
  if (ratings.length === 0) return 0;
  return ratings.reduce((a,b) => a + b, 0) / ratings.length;
}

// small month name helper
export function getMonthName(monthString) {
  if (!monthString) return 'Inconnu';
  const [, month] = String(monthString).split('-');
  const monthNames = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  const idx = parseInt(month, 10) - 1;
  return monthNames[idx] || 'Inconnu';
}

// Common list of Tunisian governorates (reusable)
export const governorates = [
  "Ariana",
  "Béja",
  "Ben Arous",
  "Bizerte",
  "Gabès",
  "Gafsa",
  "Jendouba",
  "Kairouan",
  "Kasserine",
  "Kebili",
  "Le Kef",
  "Mahdia",
  "La Manouba",
  "Médenine",
  "Monastir",
  "Nabeul",
  "Sfax",
  "Sidi Bouzid",
  "Siliana",
  "Sousse",
  "Tataouine",
  "Tozeur",
  "Tunis",
  "Zaghouan",
];

// Libellés FR au pluriel pour les types de professionnels (valeurs brutes en base)
const PROFESSION_TYPE_LABELS = {
  artisan: "Artisans",
  artisant: "Artisans",
  company: "Entreprises",
  societe: "Entreprises",
  "société": "Entreprises",
  concepteur: "Concepteurs",
  executeur: "Exécuteurs",
  "exécuteur": "Exécuteurs",
  fournisseur: "Fournisseurs",
};

// Renvoie le libellé FR pluriel d'un type ; sinon capitalise + ajoute un "s"
export function professionTypeLabel(value) {
  const v = String(value || '').trim();
  if (!v) return v;
  const key = v.toLowerCase();
  if (PROFESSION_TYPE_LABELS[key]) return PROFESSION_TYPE_LABELS[key];
  const cap = v.charAt(0).toUpperCase() + v.slice(1);
  return /s$/i.test(cap) ? cap : `${cap}s`;
}

// Construit les options { value, label } pour le menu déroulant des types
export function professionTypeOptions(values = []) {
  return values.map((v) => ({ value: v, label: professionTypeLabel(v) }));
}

// Libellés FR au singulier (pour afficher le type d'UN professionnel)
const PROFESSION_TYPE_LABELS_SINGULAR = {
  artisan: "Artisan",
  artisant: "Artisan",
  company: "Entreprise",
  societe: "Entreprise",
  "société": "Entreprise",
  concepteur: "Concepteur",
  executeur: "Exécuteur",
  "exécuteur": "Exécuteur",
  fournisseur: "Fournisseur",
};

// Renvoie le libellé FR singulier d'un type ; sinon capitalise simplement
export function professionTypeLabelSingular(value) {
  const v = String(value || '').trim();
  if (!v) return v;
  const key = v.toLowerCase();
  if (PROFESSION_TYPE_LABELS_SINGULAR[key]) return PROFESSION_TYPE_LABELS_SINGULAR[key];
  return v.charAt(0).toUpperCase() + v.slice(1);
}

// Avatar helpers (initials + deterministic background color)
export function getInitials(first, last) {
  const a = (first || '').trim();
  const b = (last || '').trim();
  const i1 = a ? a.charAt(0).toUpperCase() : '';
  const i2 = b ? b.charAt(0).toUpperCase() : '';
  return (i1 + i2) || (a ? a.charAt(0).toUpperCase() : '') || (b ? b.charAt(0).toUpperCase() : '');
}

const defaultAvatarPalette = ['#34495e'];
export function avatarColorFor(seed) {
  const s = String(seed || '').trim();
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
  }
  const idx = Math.abs(hash) % defaultAvatarPalette.length;
  return defaultAvatarPalette[idx];
}

// Export a JSON array to Excel (.xlsx) using XLSX utils
export function exportToExcel(data, sheetName = 'Sheet1', filename = 'export.xlsx') {
  if (!data) return;
  try {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, filename);
  } catch (err) {
    console.error('exportToExcel error', err);
  }
}

// Simple print helper: open a new window, write HTML, trigger print
export function openPrintWindow(html, title = '') {
  try {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`<!doctype html><html><head><title>${title}</title></head><body>${html}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      try { printWindow.print(); } catch (e) { }
    }, 250);
  } catch (e) {
    console.error('openPrintWindow failed', e);
  }
}

// Provide a named default export to satisfy import/no-anonymous-default-export
const hooks = {
  useResizeObserverErrorSuppression,
  useResponsiveDrawerWidth,
  formatCategories,
  parseCategories,
  parseQualifications,
  parseServices,
  parseGalleryImages,
  calculateAverageRating,
  getMonthName,
  governorates,
  professionTypeLabel,
  professionTypeLabelSingular,
  professionTypeOptions,
  getInitials,
  avatarColorFor,
  exportToExcel,
  openPrintWindow,
};

export default hooks;
